import { useCallback, useMemo } from 'react';
import useSearchParams from './useSearchParams';

/**
 * Hook that can be used the same as `useState` but stores the values in the query params.
 * If there are multiple query params with the same key the first occurence is used.
 *
 * @param key Key of the query params
 * @param defaultValue Default value if the query param is not set or `fromString` returns `undefined`
 * @param fromString Converts the value in the search param to an object of type `T`. If `undefined` is returned the value cannot be converted and `defaultValue` is used.
 * @param toString Converts an variable of type `T` to a string that can be stored in the search params. If `null` is returned, the search param does not have a value.
 * @param action Can be `'push'` or `'replace'` depending on whether a new history state should be pushed or the current one should be replaced.
 */
export default function useSearchParam<T>(
    key: string,
    defaultValue: T,
    fromString: (term: string | null) => T | undefined,
    toString: (v: T) => string | null | undefined,
    action: 'push' | 'replace' = 'push'
): [T, (v: T) => void] {
    const [params, setParams] = useSearchParams(action);

    const value = useMemo(() => {
        const param = params.find(p => p.key === key);
        if (param === undefined) {
            return defaultValue;
        }
        const parsedValue = fromString(param.value);
        return parsedValue === undefined ? defaultValue : parsedValue;
    }, [params, fromString, defaultValue, key]);

    const setValue = useCallback(
        (v: T) => {
            setParams([{ key, value: toString(v) }]);
        },
        [key, setParams, toString]
    );

    return [value, setValue];
}
